import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { UntypedFormBuilder, type UntypedFormGroup } from "@angular/forms";
import type { Member } from "@app/card-modal-component/member-interface";
import type { IBaseCard, IBaseT1Card } from "@app/views-interfaces";
import type { ICardModel } from "@app/views-interfaces/card-model.interface";
import type { IiFirmDeliveryMethod } from "@shared/ifirm-delivery-methods";
import { TranslateObject } from "@app/views-interfaces/translate-object.interface";
import type { IDashboardModel } from "@app/views-interfaces/dashboard-model.interface";
import type { ICardIfirmContactInformation } from "@app/views-interfaces/card-ifirm-contact-information.interface";
import { ApplicationType } from "@app/views-interfaces/ApplicationType.enum";
import { AuthService } from "@shared/auth";
import { LocalStorageService } from "@shared/local-storage";

@Injectable({
    providedIn: "root",
})
export class CardModalService {
    private readonly fb = inject(UntypedFormBuilder);
    private readonly authService = inject(AuthService);

    public isSidebarCollapsed = signal<boolean>(false);
    public totalTagsBodySpace = signal<number>(440);
    public members = signal<Member[]>([]);
    public primaryMember = computed(() => this.members().find((member) => member.memberType === "Taxpayer"));
    public clickedMembers = signal<null | string>(null);
    public showLoader = signal<boolean>(false);
    public saveForm = signal<boolean>(false);
    public closeForm = signal<boolean>(false);
    public returnStatusDefaultValue = signal<boolean>(false);
    public assignedStatusDefaultValue = signal<boolean>(false);
    public currentCard = signal<IBaseCard | null>(null);
    public currentCardId = computed(() => this.currentCard()?.cardId ?? "");
    public filenameFull = signal<string>("");
    public openInApp = signal<number>(0);
    public isVIP = signal<boolean>(false);
    public auditWindowOpen = signal<boolean>(false);
    public openContact = signal<boolean>(false);
    public openContactAndSave = signal<boolean>(false);
    public openPortal = signal<boolean>(false);
    public openIniFirmTaxprep = signal<boolean>(false);
    public deleteCard = signal<boolean>(false);
    public cardPositionInCol = signal<{ cardId: string | null; stepId: string | null }>({ cardId: null, stepId: null });
    public cardNotFound = signal<boolean>(false);
    public showModal = signal<boolean>(false);
    public cardModel = signal<ICardModel | null>(null);
    public isiFirmDeliveryMethods = signal<boolean>(false);
    public deliveryMethods = signal<{ key: number; value: TranslateObject }[] | IiFirmDeliveryMethod[] | null>(null);
    public group1Enabled = signal<boolean>(false);
    public group2Enabled = signal<boolean>(false);
    public isiFirmTaxDashboard = signal<boolean>(false);
    public isJobsBillingDashboard = signal<boolean>(false);
    public canEditAssignee = signal<boolean>(false);
    public canContactsEditGroup = signal<boolean>(false);
    public canContactsEditGenericInformations = signal<boolean>(false);
    public currentDashboard = signal<IDashboardModel | null>(null);
    public iFirmContactInformation = signal<ICardIfirmContactInformation | null>(null);
    public cardType = signal<ApplicationType | null>(null);
    public savingChanges = signal<boolean>(false);
    public isCardChanged = signal<boolean>(false);
    public cardId = signal<string | null>(null);
    public cardModalFormGroup = signal<UntypedFormGroup>(this.fb.group({}));
    public cardIdSaving = signal<string[]>([]);
    public selectedTaxpayer = signal<Member | null>(null);
    public useiFirmUserId = signal<boolean>(false);

    public handlePendingChanges = signal<null | string>(null);
    private isLoaded = false;

    public saveChangeEvent: ((isCloseOnSave: boolean, callback?: () => void) => void | Promise<void>) | null = null;
    public memberChangedEvent: ((cardData: IBaseT1Card, member: unknown) => boolean | Promise<boolean>) | null = null;

    constructor() {
        this.loadSidebarSettingsFromLocalStorage();
        effect(() => {
            const isCollapsed = this.isSidebarCollapsed();
            if (!this.isLoaded) return;

            this.saveSidebarSettingsToLocalStorage(isCollapsed ? "collapsed" : "expand");
        });
        this.isLoaded = true;
    }

    saveSidebarSettingsToLocalStorage(value: string) {
        LocalStorageService.setItem("OpenCard_Sidebar_Collapsed_State_" + this.authService.currentUser()?.sub, value);
    }

    loadSidebarSettingsFromLocalStorage() {
        const value = localStorage.getItem("OpenCard_Sidebar_Collapsed_State_" + this.authService.currentUser()?.sub);
        if (value && value == "collapsed") {
            this.totalTagsBodySpace.set(540);
            this.isSidebarCollapsed.set(true);
        }
    }

    reset() {
        this.totalTagsBodySpace.set(440);
        this.members.set([]);
        this.clickedMembers.set(null);
        this.showLoader.set(false);
        this.saveForm.set(false);
        this.closeForm.set(false);
        this.returnStatusDefaultValue.set(false);
        this.assignedStatusDefaultValue.set(false);
        this.currentCard.set(null);
        this.filenameFull.set("");
        this.openInApp.set(0);
        this.isVIP.set(false);
        this.auditWindowOpen.set(false);
        this.openContact.set(false);
        this.openPortal.set(false);
        this.openIniFirmTaxprep.set(false);
        this.deleteCard.set(false);
        this.cardPositionInCol.set({ cardId: null, stepId: null });
        this.cardNotFound.set(false);
        this.showModal.set(false);
        this.cardModel.set(null);
        this.deliveryMethods.set(null);
        this.isiFirmDeliveryMethods.set(false);
        this.group1Enabled.set(false);
        this.group2Enabled.set(false);
        this.isiFirmTaxDashboard.set(false);
        this.isJobsBillingDashboard.set(false);
        this.canEditAssignee.set(false);
        this.canContactsEditGroup.set(false);
        this.canContactsEditGenericInformations.set(false);
        this.currentDashboard.set(null);
        this.iFirmContactInformation.set(null);
        this.cardType.set(null);
        this.savingChanges.set(false);
        this.isCardChanged.set(false);
        this.cardId.set(null);
        this.cardModalFormGroup.set(this.fb.group({}));
        this.cardIdSaving.set([]);
        this.handlePendingChanges.set(null);
        this.selectedTaxpayer.set(null);
        this.useiFirmUserId.set(false);

        this.saveChangeEvent = null;
        this.memberChangedEvent = null;
    }
}
